import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Check, EyeOff } from 'lucide-react';
import { fetchWithAuth } from '../Utils/fetchWithAuth';
import API_ENDPOINTS from '../Config/api';

/**
 * GuidedTour — overlay interattivo multi-pagina.
 * Tooltip posizionato in automatico per NON sovrapporsi mai all'elemento evidenziato.
 * Gli step vengono caricati dal DB e filtrati per servizi attivi dell'azienda.
 */

/* ═══════════════════════════════════════════════════════
   FALLBACK STEPS (usati solo se l'API fallisce)
   ═══════════════════════════════════════════════════════ */
const FALLBACK_STEPS = [
  // ─── Layout / Sidebar ───
  { target: '[data-tour="sidebar-nav"]',          titleKey: 'tour.sidebar_title',          descKey: 'tour.sidebar_desc',          placement: 'right' },
  { target: '[data-tour="navbar-notifications"]',  titleKey: 'tour.notifications_title',    descKey: 'tour.notifications_desc',    placement: 'bottom' },
  { target: '[data-tour="navbar-profile"]',        titleKey: 'tour.profile_title',          descKey: 'tour.profile_desc',          placement: 'bottom' },
  { target: '[data-tour="navbar-mfa-security"]',    titleKey: 'tour.mfa_security_title',     descKey: 'tour.mfa_security_desc',     placement: 'left', forceShow: true },

  // ─── Dashboard ───
  { target: '[data-tour="dashboard-cards"]',            titleKey: 'tour.cards_title',            descKey: 'tour.cards_desc',            placement: 'top',    route: '/dashboard' },
  { target: '[data-tour="dashboard-card-endpoints"]',   titleKey: 'tour.card_endpoints_title',   descKey: 'tour.card_endpoints_desc',   placement: 'bottom', route: '/dashboard' },
  { target: '[data-tour="dashboard-card-fileangel"]',   titleKey: 'tour.card_fileangel_title',   descKey: 'tour.card_fileangel_desc',   placement: 'bottom', route: '/dashboard' },
  { target: '[data-tour="dashboard-card-browsingangel"]', titleKey: 'tour.card_browsingangel_title', descKey: 'tour.card_browsingangel_desc', placement: 'bottom', route: '/dashboard' },
  { target: '[data-tour="dashboard-card-surveillance"]', titleKey: 'tour.card_surveillance_title', descKey: 'tour.card_surveillance_desc', placement: 'bottom', route: '/dashboard' },
  { target: '[data-tour="dashboard-card-nis2"]',        titleKey: 'tour.card_nis2_title',        descKey: 'tour.card_nis2_desc',        placement: 'bottom', route: '/dashboard' },

  // ─── Plugin Admin ───
  { target: '[data-tour="pluginadmin-table"]', titleKey: 'tour.pluginadmin_title', descKey: 'tour.pluginadmin_desc', placement: 'top', route: '/pluginadmin' },

  // ─── Compliance ───
  { target: '[data-tour="compliance-hero"]',    titleKey: 'tour.compliance_hero_title',    descKey: 'tour.compliance_hero_desc',    placement: 'top', route: '/compliance' },
  { target: '[data-tour="compliance-buttons"]', titleKey: 'tour.compliance_buttons_title', descKey: 'tour.compliance_buttons_desc', placement: 'top', route: '/compliance' },

  // ─── My Assets ───
  { target: '[data-tour="myassets-content"]', titleKey: 'tour.myassets_title', descKey: 'tour.myassets_desc', placement: 'top', route: '/myassets' },

  // ─── Cyber Log — panoramica generale + reportistica ───
  { target: '[data-tour="cyberlog-sidebar"]',      titleKey: 'tour.cyberlog_title',          descKey: 'tour.cyberlog_desc',          placement: 'right', route: '/cyberlog' },
  { target: '[data-tour="cyberlog-view-reporting"]', titleKey: 'tour.cyberlog_reporting_title', descKey: 'tour.cyberlog_reporting_desc', placement: 'top',   route: '/cyberlog', action: 'cyberlog-btn-reporting' },

  // ─── Cyber Surveillance ───
  { target: '[data-tour="surveillance-dashboard"]',  titleKey: 'tour.surveillance_dashboard_title',  descKey: 'tour.surveillance_dashboard_desc',  placement: 'bottom', route: '/cybersurveillance' },
  { target: '[data-tour="surveillance-stats"]',      titleKey: 'tour.surveillance_stats_title',      descKey: 'tour.surveillance_stats_desc',      placement: 'top',    route: '/cybersurveillance' },
  { target: '[data-tour="surveillance-tabs"]',       titleKey: 'tour.surveillance_tabs_title',       descKey: 'tour.surveillance_tabs_desc',       placement: 'top',    route: '/cybersurveillance' },
  { target: '[data-tour="surveillance-criticality"]', titleKey: 'tour.surveillance_criticality_title', descKey: 'tour.surveillance_criticality_desc', placement: 'top', route: '/cybersurveillance' },

  // ─── Networking ───
  { target: '[data-tour="networking-kpis"]',     titleKey: 'tour.networking_kpis_title',     descKey: 'tour.networking_kpis_desc',     placement: 'bottom', route: '/networking', requires_service: 'networking' },
  { target: '[data-tour="networking-topology"]', titleKey: 'tour.networking_topology_title', descKey: 'tour.networking_topology_desc', placement: 'top',    route: '/networking', requires_service: 'networking' },

  // ─── Awareness ───
  { target: '[data-tour="awareness-sidebar"]', titleKey: 'tour.awareness_sidebar_title', descKey: 'tour.awareness_sidebar_desc', placement: 'right', route: '/awareness', requires_service: 'awareness' },
  { target: '[data-tour="awareness-content"]', titleKey: 'tour.awareness_content_title', descKey: 'tour.awareness_content_desc', placement: 'left',  route: '/awareness', requires_service: 'awareness' },

  // ─── Enterprise ───
  { target: '[data-tour="enterprise-content"]', titleKey: 'tour.enterprise_title', descKey: 'tour.enterprise_desc', placement: 'top', route: '/enterpriseConsent' },
];

/* Section metadata per badge */
const SECTION_META = {
  '/dashboard':        { color: '#F97316', label: 'Dashboard' },
  '/pluginadmin':      { color: '#FB923C', label: 'Plugin Admin' },
  '/compliance':       { color: '#F97316', label: 'Compliance' },
  '/myassets':         { color: '#FB923C', label: 'My Assets' },
  '/cyberlog':         { color: '#F97316', label: 'Cyber Log' },
  '/cybersurveillance': { color: '#FB923C', label: 'Surveillance' },
  '/networking':       { color: '#F97316', label: 'Networking' },
  '/awareness':        { color: '#FB923C', label: 'Awareness' },
  '/enterpriseConsent': { color: '#F97316', label: 'Enterprise' },
};

/**
 * Converte uno step dal DB nel formato usato dal componente.
 * Se lo step viene dall'API ha titolo/desc direttamente; se dal fallback usa i18n keys.
 */
function getStepText(stepObj, lang, t) {
  if (stepObj._fromDb) {
    const suffix = lang === 'it' ? '_it' : lang === 'pt' ? '_pt' : '_en';
    return {
      title: stepObj[`title${suffix}`] || stepObj.title_en || '',
      desc:  stepObj[`description${suffix}`] || stepObj.description_en || '',
    };
  }
  // Fallback: usa chiavi i18n
  return { title: t(stepObj.titleKey), desc: t(stepObj.descKey) };
}

/** Filtra gli step in base ai servizi attivi dell'azienda */
function filterStepsByServices(steps, services) {
  if (!services) return steps;
  return steps.filter(s => {
    if (!s.requires_service) return true;
    if (s.requires_service === 'networking' && !services.networking) return false;
    if (s.requires_service === 'awareness' && !services.awareness) return false;
    return true;
  });
}

/** Normalizza uno step dal DB API nel formato interno */
function normalizeDbStep(s) {
  return {
    target: s.target,
    placement: s.placement || 'top',
    route: s.route || '',
    action: s.action || '',
    requires_service: s.requires_service || null,
    forceShow: s.target === '[data-tour="navbar-mfa-security"]',
    title_it: s.title_it,
    title_en: s.title_en,
    title_pt: s.title_pt,
    description_it: s.description_it,
    description_en: s.description_en,
    description_pt: s.description_pt,
    _fromDb: true,
  };
}

/* ═══════════════════════════════════════════════════════
   SMART TOOLTIP POSITIONING — mai sovrapporre l'elemento
   Usa altezza misurata dal ref per precisione assoluta.
   ═══════════════════════════════════════════════════════ */
const TOOLTIP_W = 300;
const GAP = 16; // spazio fra tooltip ed elemento

function rectsOverlap(a, b) {
  return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);
}

/** Calcola posizione RAW (senza clamping) per un dato placement */
function calcPlacementRaw(targetR, placement, tH) {
  const tW = TOOLTIP_W;
  let top, left;

  switch (placement) {
    case 'right':
      top  = targetR.top + targetR.height / 2 - tH / 2;
      left = targetR.right + GAP;
      break;
    case 'left':
      top  = targetR.top + targetR.height / 2 - tH / 2;
      left = targetR.left - tW - GAP;
      break;
    case 'bottom':
      top  = targetR.bottom + GAP;
      left = targetR.left + targetR.width / 2 - tW / 2;
      break;
    case 'top':
    default:
      top  = targetR.top - tH - GAP;
      left = targetR.left + targetR.width / 2 - tW / 2;
      break;
  }

  return { top, left, width: tW, height: tH, right: left + tW, bottom: top + tH };
}

/** Clamp posizione nel viewport senza modificare width/height */
function clampToViewport(pos) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const top  = Math.max(12, Math.min(pos.top,  vh - pos.height - 12));
  const left = Math.max(12, Math.min(pos.left, vw - pos.width  - 12));
  return { top, left, width: pos.width, height: pos.height, right: left + pos.width, bottom: top + pos.height };
}

function getSmartTooltipPosition(targetRect, preferredPlacement, pad, measuredH) {
  const tH = measuredH > 0 ? measuredH : 210;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Rettangolo del target (con padding spotlight)
  const tR = {
    top:    targetRect.top    - pad,
    left:   targetRect.left   - pad,
    right:  targetRect.left   + targetRect.width  + pad,
    bottom: targetRect.top    + targetRect.height + pad,
  };

  // Prova ogni placement: calcola raw → clamp → controlla overlap DOPO il clamp
  const placements = [preferredPlacement, 'bottom', 'right', 'top', 'left']
    .filter((p, i, arr) => arr.indexOf(p) === i);

  for (const pl of placements) {
    const raw = calcPlacementRaw(targetRect, pl, tH);
    const clamped = clampToViewport(raw);
    if (!rectsOverlap(clamped, tR)) {
      return { top: clamped.top, left: clamped.left, actualPlacement: pl };
    }
  }

  // Nessun placement standard funziona → prova 4 angoli del viewport
  const centerX = (tR.left + tR.right)  / 2;
  const centerY = (tR.top  + tR.bottom) / 2;

  const corners = [
    { top: 12,              left: 12 },
    { top: 12,              left: vw - TOOLTIP_W - 12 },
    { top: vh - tH - 12,    left: 12 },
    { top: vh - tH - 12,    left: vw - TOOLTIP_W - 12 },
  ];

  // Ordina per distanza dal centro del target (lontano = meglio)
  corners.sort((a, b) => {
    const dA = Math.hypot(a.left + TOOLTIP_W / 2 - centerX, a.top + tH / 2 - centerY);
    const dB = Math.hypot(b.left + TOOLTIP_W / 2 - centerX, b.top + tH / 2 - centerY);
    return dB - dA;
  });

  for (const c of corners) {
    const cRect = { top: c.top, left: c.left, right: c.left + TOOLTIP_W, bottom: c.top + tH };
    if (!rectsOverlap(cRect, tR)) {
      return { top: c.top, left: c.left, actualPlacement: 'corner' };
    }
  }

  // Ultimo resort assoluto: angolo più lontano comunque
  return { top: corners[0].top, left: corners[0].left, actualPlacement: 'corner' };
}

/* ═══════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════ */
export default function GuidedTour({ isVisible = true }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [active, setActive]         = useState(false);
  const [step, setStep]             = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [navigating, setNavigating] = useState(false);
  const [tourSteps, setTourSteps]   = useState(FALLBACK_STEPS);
  const [legalCleared, setLegalCleared] = useState(false);
  const tourReadyRef = useRef(false);  // true when loadTour says tour should start
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;

  const tooltipRef = useRef(null);
  const actionsRef = useRef({});
  const measuredHRef = useRef(210);
  const startRouteRef = useRef(undefined);
  const [measuredH, setMeasuredH] = useState(210);
  const [positionReady, setPositionReady] = useState(false);

  const servicesRef = useRef(null);

  /* ── Load onboarding status + tour steps from DB ── */
  const loadTour = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch steps from DB (already filtered by company's services)
      const stepsRes = await fetchWithAuth(API_ENDPOINTS.onboardingTourSteps);
      if (stepsRes.ok) {
        const stepsData = await stepsRes.json();
        // Salva i servizi attivi per filtrare anche i fallback
        if (stepsData.services) {
          servicesRef.current = stepsData.services;
        }
        if (stepsData.steps?.length > 0) {
          // Steps dal DB sono già filtrati dal backend, ma applichiamo anche il filtro client-side per sicurezza
          setTourSteps(filterStepsByServices(stepsData.steps.map(normalizeDbStep), stepsData.services));
        } else {
          // API ok ma nessuno step — usa fallback filtrati
          setTourSteps(filterStepsByServices(FALLBACK_STEPS, stepsData.services));
        }
      } else {
        // API fallita — filtra i fallback con i servizi noti (se disponibili)
        setTourSteps(filterStepsByServices(FALLBACK_STEPS, servicesRef.current));
      }

      // Check onboarding status
      const res = await fetchWithAuth(API_ENDPOINTS.onboardingStatus);
      if (res.ok) {
        const data = await res.json();
        if (!data.completed && !data.skipped) {
          tourReadyRef.current = true;
          // Attiva solo se i documenti legali sono già stati gestiti e l'account è visibile
          if (legalCleared && isVisibleRef.current) setActive(true);
        }
      }
    } catch (err) {
      console.warn('Tour init failed:', err);
    } finally {
      setLoading(false);
    }
  }, [legalCleared]);

  useEffect(() => { loadTour(); }, [loadTour]);

  /* ── Wait for legal documents to be cleared before starting ── */
  useEffect(() => {
    const handleLegalCleared = () => {
      setLegalCleared(true);
      if (tourReadyRef.current && isVisibleRef.current) setActive(true);
    };
    window.addEventListener('legal-docs-cleared', handleLegalCleared);
    return () => window.removeEventListener('legal-docs-cleared', handleLegalCleared);
  }, []);

  /* ── Listen for tour-reset event (from Navbar / Profilo) ── */
  useEffect(() => {
    const handleReset = (event) => {
      if (!isVisibleRef.current) return;
      startRouteRef.current = event.detail?.startRoute;
      setStep(0);
      setActive(true);
      loadTour();
    };
    window.addEventListener('tour-reset', handleReset);
    return () => window.removeEventListener('tour-reset', handleReset);
  }, [loadTour]);

  /* ── After tour loads, jump to the requested section (if any) ── */
  useEffect(() => {
    if (!active || loading || startRouteRef.current === undefined) return;
    const route = startRouteRef.current;
    startRouteRef.current = undefined;
    if (!route) return; // null = full tour from step 0
    const idx = tourSteps.findIndex(s => s.route === route);
    if (idx >= 0) {
      setStep(idx);
      if (route !== location.pathname) {
        setNavigating(true);
        navigate(route);
      }
    }
  }, [active, loading, tourSteps, location.pathname, navigate]);

  /* ── Execute step action (e.g. click CyberLog sidebar button) ── */
  const executeStepAction = useCallback((stepConfig) => {
    if (!stepConfig?.action) return;
    const el = document.querySelector(`[data-tour="${stepConfig.action}"]`);
    if (el) el.click();
  }, []);

  /* ── Navigate to step route ── */
  const navigateToStep = useCallback((stepIndex) => {
    const s = tourSteps[stepIndex];
    if (!s) return;
    if (s.route && location.pathname !== s.route) {
      setNavigating(true);
      navigate(s.route);
    } else {
      executeStepAction(s);
    }
  }, [location.pathname, navigate, executeStepAction, tourSteps]);

  /* ── Post-navigation: wait for target, then execute action ── */
  useEffect(() => {
    if (!navigating || !active) return;
    const currentStep = tourSteps[step];
    if (!currentStep) return;

    let attempts = 0;
    const maxAttempts = 40;
    const interval = setInterval(() => {
      if (attempts === 3) executeStepAction(currentStep);
      const el = document.querySelector(currentStep.target);
      if (el || attempts >= maxAttempts) {
        clearInterval(interval);
        setNavigating(false);
      }
      attempts++;
    }, 100);
    return () => clearInterval(interval);
  }, [navigating, step, active, location.pathname, executeStepAction]);

  /* ── Open / close profile dropdown for MFA step ── */
  useEffect(() => {
    if (!active) {
      window.dispatchEvent(new CustomEvent('tour-profile-menu', { detail: { open: false } }));
      return;
    }
    const currentStep = tourSteps[step];
    const needsMenu = currentStep?.target === '[data-tour="navbar-mfa-security"]';
    window.dispatchEvent(new CustomEvent('tour-profile-menu', { detail: { open: needsMenu } }));
  }, [step, active, tourSteps]);

  /* ── Position spotlight on target element ── */
  const updateTargetRect = useCallback(() => {
    if (!active || navigating) return;
    const currentStep = tourSteps[step];
    if (!currentStep) return;

    const el = document.querySelector(currentStep.target);
    if (el) {
      // Scroll senza animazione per evitare rect stale durante lo scroll
      el.scrollIntoView({ behavior: 'instant', block: 'nearest' });
      // Leggi rect DOPO lo scroll
      const rect = el.getBoundingClientRect();
      setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    } else {
      setTargetRect(null);
    }
  }, [active, step, navigating]);

  useEffect(() => {
    updateTargetRect();
    window.addEventListener('resize', updateTargetRect);
    window.addEventListener('scroll', updateTargetRect, true);
    return () => {
      window.removeEventListener('resize', updateTargetRect);
      window.removeEventListener('scroll', updateTargetRect, true);
    };
  }, [updateTargetRect]);

  useEffect(() => {
    setPositionReady(false);
    const currentStep = tourSteps[step];
    const delay = currentStep?.action ? 600 : 350;
    const timer = setTimeout(() => {
      updateTargetRect();
      setPositionReady(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [step, updateTargetRect]);

  /* ── Stabilizzazione extra: aggiorna rect più volte dopo cambio step ── */
  useEffect(() => {
    if (!active || navigating) return;
    // Aggiorna rect a intervalli per catturare layout shift
    const timers = [200, 500, 800].map(ms =>
      setTimeout(updateTargetRect, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [step, active, navigating, updateTargetRect]);

  /* ── Measure actual tooltip height BEFORE paint (useLayoutEffect) ── */
  useLayoutEffect(() => {
    if (!tooltipRef.current || !active || navigating) return;
    const h = tooltipRef.current.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - measuredHRef.current) > 4) {
      measuredHRef.current = h;
      setMeasuredH(h);
    }
  });

  /* ── Find next valid step ── */
  const findNextValidStep = useCallback((fromStep, direction = 1) => {
    let candidate = fromStep + direction;
    while (candidate >= 0 && candidate < tourSteps.length) {
      const s = tourSteps[candidate];
      if (s.forceShow) return candidate;
      if (s.route && s.route !== location.pathname) return candidate;
      if (s.action) return candidate;
      if (document.querySelector(s.target)) return candidate;
      candidate += direction;
    }
    return candidate;
  }, [location.pathname, tourSteps]);

  /* ── Navigation handlers ── */
  const goToStep = useCallback((s) => { setStep(s); navigateToStep(s); }, [navigateToStep]);

  const handleComplete = useCallback(async () => {
    setActive(false);
    try { await fetchWithAuth(API_ENDPOINTS.onboardingComplete, { method: 'POST' }); } catch {}
  }, []);

  const handleSkip = useCallback(async () => {
    setActive(false);
    try { await fetchWithAuth(API_ENDPOINTS.onboardingSkip, { method: 'POST' }); } catch {}
  }, []);

  const handleNext = useCallback(() => {
    if (step < tourSteps.length - 1) {
      const next = findNextValidStep(step, 1);
      if (next < tourSteps.length) goToStep(next); else handleComplete();
    } else { handleComplete(); }
  }, [step, findNextValidStep, goToStep, handleComplete]);

  const handleBack = useCallback(() => {
    if (step > 0) { const prev = findNextValidStep(step, -1); if (prev >= 0) goToStep(prev); }
  }, [step, findNextValidStep, goToStep]);

  actionsRef.current = { handleNext, handleBack, handleSkip };

  /* ── Keyboard navigation ── */
  useEffect(() => {
    if (!active || navigating) return;
    const onKey = (e) => {
      switch (e.key) {
        case 'Escape':     actionsRef.current.handleSkip(); break;
        case 'ArrowRight': actionsRef.current.handleNext(); break;
        case 'ArrowLeft':  actionsRef.current.handleBack(); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, navigating]);

  /* ════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════ */
  if (loading || !active) return null;

  const currentStep = tourSteps[step];
  const isLast  = step === tourSteps.length - 1;
  const isFirst = step === 0;
  const section = SECTION_META[currentStep.route] || { color: '#F97316', label: 'Layout' };
  const progress = ((step + 1) / tourSteps.length) * 100;
  const pad = 10;

  /* ── Loading overlay during page navigation ── */
  if (navigating) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60" style={{ backdropFilter: 'blur(4px)' }} />
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className="relative z-[10000] flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-full animate-spin"
            style={{ border: '3px solid rgba(249,115,22,0.2)', borderTopColor: '#F97316' }} />
          <p className="text-sm font-medium text-white/70 tracking-wide">
            {t('tour.loading', 'Caricamento...')}
          </p>
        </motion.div>
      </div>,
      document.body
    );
  }

  /* ── Smart position: mai sovrapporre l'elemento ── */
  const tooltipPos = targetRect
    ? getSmartTooltipPosition(targetRect, currentStep.placement, pad, measuredH)
    : { top: window.innerHeight / 2 - 105, left: window.innerWidth / 2 - 150, actualPlacement: 'bottom' };

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-guided-tour style={{ pointerEvents: 'auto' }}>

      {/* ── Dark overlay with spotlight cutout ── */}
      <svg className="fixed inset-0 w-full h-full" style={{ pointerEvents: 'none' }}
        xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - pad} y={targetRect.top - pad}
                width={targetRect.width + pad * 2} height={targetRect.height + pad * 2}
                rx="12" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.6)"
          mask="url(#tour-spotlight-mask)" />
      </svg>

      {/* ── Animated spotlight glow — yellow/amber gradient ── */}
      {targetRect && (
        <motion.div
          key={`glow-${step}`}
          className="fixed pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            top:    targetRect.top  - pad,
            left:   targetRect.left - pad,
            width:  targetRect.width  + pad * 2,
            height: targetRect.height + pad * 2,
            boxShadow: [
              '0 0 0 2px rgba(250,204,21,0.65), 0 0 18px 4px rgba(250,204,21,0.10), 0 0 36px 8px rgba(249,115,22,0.05)',
              '0 0 0 2px rgba(249,115,22,0.65), 0 0 18px 4px rgba(249,115,22,0.12), 0 0 36px 8px rgba(250,204,21,0.06)',
              '0 0 0 2px rgba(250,204,21,0.65), 0 0 18px 4px rgba(250,204,21,0.10), 0 0 36px 8px rgba(249,115,22,0.05)',
            ],
          }}
          transition={{
            boxShadow: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' },
            opacity:    { duration: 0.3 },
            top:        { duration: 0.3, ease: 'easeOut' },
            left:       { duration: 0.3, ease: 'easeOut' },
            width:      { duration: 0.3, ease: 'easeOut' },
            height:     { duration: 0.3, ease: 'easeOut' },
          }}
          style={{ borderRadius: 12 }}
        />
      )}

      {/* ── Click blocker ── */}
      <div className="fixed inset-0" onClick={(e) => e.stopPropagation()} />

      {/* ── Tooltip ── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          ref={tooltipRef}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: positionReady ? 1 : 0, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className="fixed z-[10000]"
          style={{
            top: tooltipPos.top, left: tooltipPos.left,
            width: TOOLTIP_W, maxWidth: 'calc(100vw - 24px)',
            willChange: 'transform, opacity',
          }}
        >
          {/* ── Gradient border (yellow → amber → gold) ── */}
          <div className="rounded-2xl"
            style={{
              padding: '1.5px',
              background: 'linear-gradient(135deg, rgba(250,204,21,0.55), rgba(249,115,22,0.50), rgba(234,88,12,0.4))',
            }}>
            <div className="rounded-2xl"
              style={{
                padding: '14px 16px',
                background: 'linear-gradient(160deg, rgba(15,23,42,0.97) 0%, rgba(30,41,59,0.97) 100%)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.5), 0 0 25px rgba(249,115,22,0.04)',
              }}>

              {/* ── Header ── */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                  {/* Step counter */}
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    height: 22, minWidth: 44, padding: '0 7px',
                    borderRadius: 9999, fontSize: 10, fontWeight: 700, letterSpacing: '0.02em',
                    background: 'linear-gradient(135deg, rgba(250,204,21,0.12), rgba(249,115,22,0.10))',
                    color: '#FACC15',
                    border: '1px solid rgba(250,204,21,0.18)',
                  }}>
                    {step + 1} / {tourSteps.length}
                  </div>

                  {/* Section badge */}
                  {currentStep.route && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center',
                      height: 22, padding: '0 8px', gap: 4,
                      borderRadius: 9999, fontSize: 10, fontWeight: 600,
                      background: 'rgba(249,115,22,0.08)',
                      color: '#FB923C',
                      border: '1px solid rgba(249,115,22,0.15)',
                    }}>
                      <span style={{ width: 5, height: 5, borderRadius: 9999, background: '#FB923C' }} />
                      {section.label}
                    </div>
                  )}
                </div>

                {/* Close */}
                <button onClick={handleSkip} title={t('tour.skip')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 26, height: 26, borderRadius: 8,
                    color: 'rgba(255,255,255,0.4)', background: 'transparent',
                    border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; }}>
                  <X size={13} />
                </button>
              </div>

              {/* ── Progress bar ── */}
              <div style={{ width: '100%', height: 2, borderRadius: 9999, background: 'rgba(255,255,255,0.04)', marginBottom: 10, overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  style={{
                    height: '100%', borderRadius: 9999,
                    background: 'linear-gradient(90deg, #FACC15, #F97316, #EA580C)',
                  }}
                />
              </div>

              {/* ── Title & description ── */}
              {(() => { const txt = getStepText(currentStep, i18n.language, t); return (<>
              <h3 style={{ margin: '0 0 4px 0', fontSize: 14, fontWeight: 600, color: '#f8fafc', lineHeight: 1.35 }}>
                {txt.title}
              </h3>
              <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>
                {txt.desc}
              </p>
              </>); })()}

              {/* ── Footer buttons ── */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                {/* Back */}
                <button onClick={handleBack} disabled={isFirst}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 3,
                    padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 500,
                    color: isFirst ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.45)',
                    background: 'transparent',
                    border: isFirst ? '1px solid transparent' : '1px solid rgba(255,255,255,0.07)',
                    cursor: isFirst ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!isFirst) { e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}}
                  onMouseLeave={(e) => { if (!isFirst) { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; e.currentTarget.style.background = 'transparent'; }}}>
                  <ChevronLeft size={14} />
                  {t('tour.back')}
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {!isLast && (
                    /* Skip (solo se non siamo all'ultimo step) */
                    <button onClick={handleSkip}
                      style={{
                        padding: '6px 4px', fontSize: 11,
                        color: 'rgba(255,255,255,0.5)', background: 'transparent',
                        border: 'none', cursor: 'pointer', transition: 'color 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}>
                      {t('tour.skip')}
                    </button>
                  )}

                  {isLast ? (
                    /* ── Due bottoni finali ── */
                    <>
                      {/* Non mostrare più (completa permanentemente) */}
                      <button onClick={handleComplete}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 500,
                          color: 'rgba(255,255,255,0.55)',
                          border: '1px solid rgba(255,255,255,0.10)',
                          background: 'rgba(255,255,255,0.04)',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}>
                        <EyeOff size={12} />
                        {t('tour.end_permanently', 'Non mostrare più')}
                      </button>
                      {/* Termina tour (skip — può essere riavviato) */}
                      <button onClick={handleSkip}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                          color: '#fff',
                          border: 'none', cursor: 'pointer', transition: 'filter 0.15s',
                          background: 'linear-gradient(135deg, #10b981, #059669)',
                          boxShadow: '0 3px 12px rgba(16,185,129,0.3)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}>
                        <Check size={14} />
                        {t('tour.end_tour', 'Termina tour')}
                      </button>
                    </>
                  ) : (
                    /* Next */
                    <button onClick={handleNext}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        color: '#1c1917',
                        border: 'none', cursor: 'pointer', transition: 'filter 0.15s',
                        background: 'linear-gradient(135deg, #FACC15, #F97316)',
                        boxShadow: '0 3px 12px rgba(249,115,22,0.25)',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.12)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}>
                      {t('tour.next')}<ChevronRight size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* ── Keyboard hints ── */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                marginTop: 10, paddingTop: 8,
                borderTop: '1px solid rgba(255,255,255,0.04)',
              }}>
                {[{ keys: ['←', '→'], label: t('tour.keyboard_nav', 'naviga') },
                  { keys: ['ESC'],     label: t('tour.keyboard_skip', 'esci') }
                ].map((hint, i) => (
                  <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                    {hint.keys.map((k) => (
                      <kbd key={k} style={{
                        padding: '1px 4px', borderRadius: 3,
                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                        fontSize: 9, fontFamily: 'monospace', color: 'rgba(255,255,255,0.55)',
                      }}>{k}</kbd>
                    ))}
                    {hint.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body
  );
}
